import { MESSAGE_TEMPLATES } from '@fruitful/guidance';
import type { MessageTemplate } from '@fruitful/types';
import type { GroupChannel } from '@sendbird/chat/groupChannel';
import { useAtomValue } from 'jotai';
import { FilterMatchMode } from 'primereact/api';
import { Button as PRButton } from 'primereact/button';
import { Column } from 'primereact/column';
import { confirmDialog } from 'primereact/confirmdialog';
import { DataTable } from 'primereact/datatable';
import React, { type RefObject, useCallback, useState } from 'react';
import {
    BodyLargeRegular,
    BodySmallEmphasized,
    ButtonVariant,
    H2Regular,
    XStack,
    YStack,
} from 'smoothie';

import { Button } from '../../../common/components/GroveButtons';
import { GroveSimpleModal } from '../../../common/components/GroveSimpleModal';
import { SearchInput } from '../../../common/components/SearchInput';
import { formatToFullPhoneNumber } from '../../../common/utils/formatting';
import { allGuidesAtom } from '../../../entities/entityAtoms';
import type { Member, MembershipSummary } from '../../../entities/entityTypes';
import { getSoloOrJointMemberName } from '../../../entities/membership/membershipUtils';
import { TrackedEvent } from '../../../logger/Events';
import { trackEvent } from '../../../logger/Logger';
import { useMembershipOfSendbirdChannel } from '../../hooks/useMembershipOfSendbirdChannel';

export enum MessageTemplatesModalMode {
    DRAFT = 'drafting_message',
    MANAGE = 'managing_templates',
}

type MessageInputSetter = (
    message: string,
    messageInputElement: HTMLDivElement,
    append?: boolean,
) => void;

export const DraftFromTemplate = ({
    messageInputRef,
    currentChannel,
}: {
    messageInputRef: RefObject<HTMLDivElement>;
    currentChannel: GroupChannel | null;
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const membership = useMembershipOfSendbirdChannel(currentChannel);

    const setInputText: MessageInputSetter = useCallback(
        (message, messageInputElement, append = false) => {
            message = append
                ? `${messageInputElement.innerText}\n\n${message}`
                : message;
            messageInputElement.innerText = message;
        },
        [],
    );

    const handleExistingText = useCallback(
        (
            message: string,
            messageInputElement: HTMLDivElement,
            append: MessageInputSetter,
            replace: MessageInputSetter,
        ) => {
            confirmDialog({
                header: 'Applying message template',
                message:
                    'You already have a message in progress. Do you want to replace it or append to it?',
                acceptLabel: 'Append',
                rejectLabel: 'Replace',
                accept: () => {
                    append(message, messageInputElement);
                },
                reject: () => {
                    replace(message, messageInputElement);
                },
                visible: true,
            });
        },
        [],
    );

    const setMessageDraftFromTemplate = useCallback(
        (messageDraftFromTemplate: string) => {
            const messageInputElement = messageInputRef.current;

            if (messageInputElement != null) {
                const append: MessageInputSetter = (
                    message,
                    messageInputElement,
                ) => {
                    setInputText(
                        message,
                        messageInputElement,
                        /* append */ true,
                    );
                };
                const replace = setInputText;

                if (messageInputElement.innerText.trim() === '') {
                    setInputText(messageDraftFromTemplate, messageInputElement);
                } else {
                    handleExistingText(
                        messageDraftFromTemplate,
                        messageInputElement,
                        append,
                        replace,
                    );
                }
            }
        },
        [],
    );

    return (
        <XStack paddingHorizontal={14} marginBottom={-20}>
            <PRButton
                size="small"
                severity="secondary"
                link
                onClick={() => {
                    setIsModalOpen(true);
                }}
            >
                Draft from template
            </PRButton>
            <MessageTemplatesModal
                mode={MessageTemplatesModalMode.DRAFT}
                membership={membership}
                isOpen={isModalOpen}
                selectButtonLabel="Draft message"
                onTemplateSelect={(template) => {
                    setMessageDraftFromTemplate(template.template);
                    setIsModalOpen(false);
                }}
                closeModal={() => {
                    setIsModalOpen(false);
                }}
            />
        </XStack>
    );
};

export const MessageTemplatesModal = ({
    mode,
    membership,
    isOpen,
    selectButtonLabel,
    onTemplateSelect,
    closeModal,
}: {
    mode: MessageTemplatesModalMode;
    isOpen: boolean;
    selectButtonLabel: string;
    onTemplateSelect: (template: MessageTemplate) => void;
    closeModal: () => void;
    membership?: MembershipSummary | null;
}) => {
    // Template variable placeholders
    const MEMBER_NAME_VAR = '**[MEMBER_NAME]**';
    const GUIDE_NAME_VAR = '**[GUIDE_NAME]**';
    const PRIMARY_MEMBER_FIRST_NAME_VAR = '**[PRIMARY_MEMBER_FIRST_NAME]**';
    const PRIMARY_MEMBER_LAST_NAME_VAR = '**[PRIMARY_MEMBER_LAST_NAME]**';
    const PRIMARY_MEMBER_EMAIL_VAR = '**[PRIMARY_MEMBER_EMAIL]**';
    const PRIMARY_MEMBER_PHONE_VAR = '**[PRIMARY_MEMBER_PHONE]**';

    const memberName = getSoloOrJointMemberName(
        membership?.users as Array<Partial<Member> | undefined> | undefined,
    );
    const guides = useAtomValue(allGuidesAtom);
    const guideName = guides.find(
        (guide) => guide.id === membership?.guideId,
    )?.firstName;

    // Membership summaries only expose users without type; use the first user.
    const primaryMember = membership?.users?.[0];
    const primaryMemberFirstName = primaryMember?.firstName ?? '';
    const primaryMemberLastName = primaryMember?.lastName ?? '';
    const primaryMemberEmail = primaryMember?.email ?? '';
    // Format phone to 1XXXXXXXXXX format (no + sign) as required by referral codes in ACCC links.
    const primaryMemberPhone = (() => {
        const phoneNumber = primaryMember?.phoneNumber?.trim();
        if (phoneNumber == null || phoneNumber === '') {
            return '';
        }
        try {
            return formatToFullPhoneNumber(phoneNumber).replace('+', '');
        } catch {
            return '';
        }
    })();

    const messageTemplates =
        membership == null
            ? MESSAGE_TEMPLATES
            : MESSAGE_TEMPLATES.map((messageTemplate) => ({
                  ...messageTemplate,
                  template: messageTemplate.template
                      .replaceAll(
                          MEMBER_NAME_VAR,
                          memberName ?? MEMBER_NAME_VAR,
                      )
                      .replaceAll(GUIDE_NAME_VAR, guideName ?? GUIDE_NAME_VAR)
                      .replaceAll(
                          PRIMARY_MEMBER_FIRST_NAME_VAR,
                          primaryMemberFirstName,
                      )
                      .replaceAll(
                          PRIMARY_MEMBER_LAST_NAME_VAR,
                          primaryMemberLastName,
                      )
                      .replaceAll(PRIMARY_MEMBER_EMAIL_VAR, primaryMemberEmail)
                      .replaceAll(PRIMARY_MEMBER_PHONE_VAR, primaryMemberPhone),
              }));

    return (
        <GroveSimpleModal
            isOpen={isOpen}
            hasCloseButton={true}
            onClose={closeModal}
        >
            <YStack
                marginHorizontal={16}
                marginTop={32}
                width="90vw"
                maxWidth={1600}
                gap={48}
            >
                <MessageTemplatesTable
                    messageTemplates={messageTemplates}
                    mode={mode}
                    selectButtonLabel={selectButtonLabel}
                    onTemplateSelect={onTemplateSelect}
                />
                <XStack justifyContent="center">
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        size="small"
                        onPress={closeModal}
                        width="auto"
                    >
                        Close
                    </Button>
                </XStack>
            </YStack>
        </GroveSimpleModal>
    );
};

interface FilterValue {
    value: string | null;
    matchMode: FilterMatchMode;
}

const MessageTemplatesTable = ({
    messageTemplates,
    mode,
    selectButtonLabel,
    onTemplateSelect,
}: {
    messageTemplates: MessageTemplate[];
    mode: MessageTemplatesModalMode;
    selectButtonLabel: string;
    onTemplateSelect: (template: MessageTemplate) => void;
}) => {
    const [searchString, setSearchString] = useState('');
    const [filters, setFilters] = useState<Record<string, FilterValue>>({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        category: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
    });

    const onSearchChange = useCallback((value: string) => {
        setSearchString(value);
        setFilters((prev) => ({
            ...prev,
            global: { value, matchMode: FilterMatchMode.CONTAINS },
        }));
    }, []);

    const onTemplateSelectClick = useCallback((template: MessageTemplate) => {
        onTemplateSelect(template);
        trackEvent(TrackedEvent.GuideMessageTemplateSelected, {
            templateId: template.id,
            templateCategory: template.category,
            templateName: template.name,
            mode,
            // TODO if we support guide-modifiable templates, we'd want to track whether this is global or guide-specific
        });
    }, []);

    const CategoryAndNameRenderer = useCallback((data: MessageTemplate) => {
        return (
            <YStack marginTop={8} gap={8} justifyContent="flex-start">
                <BodyLargeRegular>{data.category}</BodyLargeRegular>
                <BodySmallEmphasized style={{ lineHeight: '1.1' }}>
                    {data.name}
                </BodySmallEmphasized>
            </YStack>
        );
    }, []);

    const TemplateSelectRenderer = useCallback(
        (template: MessageTemplate) => {
            return (
                <PRButton
                    size="small"
                    label={selectButtonLabel}
                    onClick={() => {
                        onTemplateSelectClick(template);
                    }}
                />
            );
        },
        [selectButtonLabel],
    );

    return (
        <YStack gap={16} marginTop={16}>
            <XStack
                width="100%"
                justifyContent="space-between"
                alignItems="center"
            >
                <H2Regular>Message Templates</H2Regular>
                <SearchInput
                    value={searchString}
                    placeholder="Category, name, template contents..."
                    onChangeText={onSearchChange}
                />
            </XStack>
            <DataTable
                dataKey="id"
                value={messageTemplates}
                size="small"
                scrollable
                scrollHeight="60vh"
                tableStyle={{ width: '100%' }}
                sortField="group"
                filters={filters}
            >
                {/* Including hidden "name" column so these values are searchable */}
                <Column field="name" hidden />
                <Column
                    field="category"
                    header="Category & Name"
                    filter
                    sortable
                    style={{ verticalAlign: 'top' }}
                    body={CategoryAndNameRenderer}
                />
                <Column
                    field="template"
                    header="Template"
                    sortable
                    style={{
                        fontSize: 13,
                        lineHeight: 1,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                    }}
                />
                <Column body={TemplateSelectRenderer} />
            </DataTable>
        </YStack>
    );
};

// MessageTemplate interface and MESSAGE_TEMPLATES array are now imported from @fruitful/guidance
// This allows them to be shared with llm-service and other applications
